import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  fetchRedditHistory,
  fetchRedditSubreddits,
  fetchRedditPosts,
  selectRedditVerlauf,
  selectRedditVerlaufStatus,
  selectRedditSubreddits,
  selectRedditSubredditsStatus,
  selectRedditPosts,
  selectRedditPostsStatus,
} from 'modules/reddit/features/redditSlice';
import {
  selectAktie,
  fetchAktieDetail,
  selectAktieDetail,
  selectAktieDetailStatus,
} from 'modules/aktien';
import { formatZahl, formatKurs, formatDatum } from 'core/utils/format';
import { farben, Kachel, Abschnitt, LeerHinweis } from 'core/components/ui';
import BackToOverview from 'core/components/BackToOverview';

const achseStil = { fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fill: farben.inkMuted };
const TAG_MS = 86400000;

/** Zeitstempel → kompakter Achsen-Tick „TT.MM HH:mm". */
const tickZeit = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/** Datum → kompakter Achsen-Tick „TT.MM.JJ". */
const tickDatum = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/** Epoch-ms → Wert für ein <input type="date"> (lokales Datum). */
const msZuDateInput = (ms) => {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** <input type="date">-Wert → Epoch-ms (lokale Mitternacht). */
const dateInputZuMs = (wert) => {
  const ms = new Date(`${wert}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** Reiter-Button der Tab-Navigation. */
function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** Tooltip für den Erwähnungs-Verlauf-Chart. */
function VerlaufTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border border-line bg-paper-soft px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-mono text-ink-muted">{new Date(label).toLocaleString('de-DE')}</div>
      <div className="font-mono text-ink">Erwähnungen: {formatZahl(p.mentions, 0)}</div>
      <div className="font-mono text-ink-muted">Upvotes: {formatZahl(p.upvotes, 0)}</div>
    </div>
  );
}

/** Tooltip für den Kurs-Chart. */
function KursTooltip({ active, payload, label, currency }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-lg border border-line bg-paper-soft px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-mono text-ink-muted">{formatDatum(label)}</div>
      <div className="font-mono text-ink">Schluss: {formatKurs(payload[0]?.value, currency)}</div>
    </div>
  );
}

/**
 * Zeitraum-Auswahl (von–bis) mit je einem Schieberegler und einem Datumsfeld je
 * Grenze. Die Werte sind Epoch-ms; die Grenzen werden gegenseitig geklammert.
 */
function ZeitraumRegler({ min, max, von, bis, onChange, onReset }) {
  const setzeVon = (ms) => onChange(Math.min(Math.max(ms, min), bis), bis);
  const setzeBis = (ms) => onChange(von, Math.max(Math.min(ms, max), von));
  const vollerBereich = von <= min && bis >= max;

  return (
    <div className="mb-6 rounded-xl border border-line bg-paper-soft p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Zeitraum
        </span>
        {!vollerBereich && (
          <button
            type="button"
            onClick={onReset}
            className="font-mono text-xs text-brand-700 transition-colors hover:text-brand-600"
          >
            Zurücksetzen
          </button>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <GrenzenRegler
          label="Von"
          min={min}
          max={max}
          wert={von}
          onSlider={setzeVon}
          onDatum={(ms) => ms != null && setzeVon(ms)}
        />
        <GrenzenRegler
          label="Bis"
          min={min}
          max={max}
          wert={bis}
          onSlider={setzeBis}
          onDatum={(ms) => ms != null && setzeBis(ms)}
        />
      </div>
    </div>
  );
}

/** Eine Grenze des Zeitraums: Schieberegler + Datumsfeld. */
function GrenzenRegler({ label, min, max, wert, onSlider, onDatum }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">{label}</span>
        <input
          type="date"
          value={msZuDateInput(wert)}
          min={msZuDateInput(min)}
          max={msZuDateInput(max)}
          onChange={(e) => onDatum(dateInputZuMs(e.target.value))}
          className="rounded border border-line bg-paper px-2 py-1 font-mono text-xs text-ink focus:border-brand-500 focus:outline-none"
        />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={TAG_MS}
        value={wert}
        onChange={(e) => onSlider(Number(e.target.value))}
        className="w-full accent-brand-600"
      />
    </label>
  );
}

/**
 * Aufschlüsselung + Top-Beiträge je Subreddit als Karten: Erwähnungs-Balken oben,
 * darunter die besten Beiträge (Titel, Upvotes, Datum, Kommentare).
 */
function SubredditKarten({ eintraege, maxMentions, postsStatus }) {
  return (
    <div className="space-y-4">
      {eintraege.map((e) => (
        <div key={e.subreddit} className="rounded-xl border border-line bg-paper-soft p-4">
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <span className="font-mono text-sm font-semibold text-ink">r/{e.subreddit}</span>
            <span className="font-mono text-sm tabular-nums text-ink">
              {e.mentions != null ? formatZahl(e.mentions, 0) : '–'}
              <span className="ml-2 text-xs font-normal text-ink-muted">
                {e.upvotes != null ? `${formatZahl(e.upvotes, 0)} Upvotes` : ''}
              </span>
            </span>
          </div>
          <div className="mb-3 h-2 overflow-hidden rounded-full bg-ink/5">
            <div
              className="h-full rounded-full"
              style={{
                width: `${((e.mentions || 0) / maxMentions) * 100}%`,
                backgroundColor: farben.brand,
                opacity: 0.65,
              }}
            />
          </div>
          <PostListe posts={e.posts} laedt={postsStatus === 'loading'} />
        </div>
      ))}
    </div>
  );
}

/** Liste der Top-Beiträge eines Subreddits (bis zu 3). */
function PostListe({ posts, laedt }) {
  if (laedt) {
    return <p className="text-xs text-ink-muted">Lade Beiträge…</p>;
  }
  if (!posts || posts.length === 0) {
    return <p className="text-xs text-ink-muted">Keine passenden Beiträge gefunden.</p>;
  }
  return (
    <ul className="space-y-2">
      {posts.map((p, i) => (
        <li key={p.url || i}>
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg border border-line bg-paper px-3 py-2 transition-colors hover:bg-ink/[0.03]"
          >
            <div className="text-sm text-ink">{p.titel}</div>
            <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-[11px] text-ink-muted">
              <span>▲ {formatZahl(p.upvotes, 0)}</span>
              <span>💬 {formatZahl(p.kommentare, 0)}</span>
              {p.erstelltAm && <span>{new Date(p.erstelltAm).toLocaleDateString('de-DE')}</span>}
              {p.autor && <span>u/{p.autor}</span>}
            </div>
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Verlauf-Tab: gemeinsamer Zeitraum-Regler über Erwähnungs- und Kurs-Chart. */
function VerlaufTab({ reihe, kurse, currency, inDatenbank, kursStatus }) {
  const grenzen = useMemo(() => {
    const zeiten = [
      ...reihe.map((p) => new Date(p.erfasstAm).getTime()),
      ...kurse.map((p) => new Date(p.datum).getTime()),
    ].filter((t) => !Number.isNaN(t));
    if (zeiten.length === 0) return null;
    return { min: Math.min(...zeiten), max: Math.max(...zeiten) };
  }, [reihe, kurse]);

  const [von, setVon] = useState(null);
  const [bis, setBis] = useState(null);

  // Regler auf den vollen Bereich setzen, sobald die Datengrenzen feststehen.
  useEffect(() => {
    if (grenzen) {
      setVon(grenzen.min);
      setBis(grenzen.max);
    }
  }, [grenzen]);

  const imFenster = (iso) => {
    const t = new Date(iso).getTime();
    if (von == null || bis == null) return true;
    return t >= von && t <= bis + TAG_MS - 1;
  };
  const reiheGefiltert = reihe.filter((p) => imFenster(p.erfasstAm));
  const kurseGefiltert = kurse.filter((p) => imFenster(p.datum));

  return (
    <>
      {grenzen && von != null && bis != null && (
        <ZeitraumRegler
          min={grenzen.min}
          max={grenzen.max}
          von={von}
          bis={bis}
          onChange={(v, b) => {
            setVon(v);
            setBis(b);
          }}
          onReset={() => {
            setVon(grenzen.min);
            setBis(grenzen.max);
          }}
        />
      )}

      <Abschnitt
        titel="Erwähnungen & Upvotes über die Zeit"
        aktion={
          <div className="hidden gap-3 sm:flex">
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: farben.brand }} />
              Erwähnungen
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
              <span className="inline-block h-0.5 w-4" style={{ backgroundColor: farben.gold }} />
              Upvotes
            </span>
          </div>
        }
      >
        {reiheGefiltert.length <= 1 ? (
          <LeerHinweis>Für den gewählten Zeitraum liegen zu wenige Messpunkte vor.</LeerHinweis>
        ) : (
          <div className="rounded-xl border border-line bg-paper-soft p-2">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={reiheGefiltert} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="erfasstAm" tickFormatter={tickZeit} tick={achseStil} minTickGap={48} stroke={farben.line} />
                <YAxis yAxisId="mentions" tick={achseStil} width={48} stroke={farben.line} tickFormatter={(v) => formatZahl(v, 0)} />
                <YAxis yAxisId="upvotes" orientation="right" tick={achseStil} width={48} stroke={farben.line} tickFormatter={(v) => formatZahl(v, 0)} />
                <Tooltip content={<VerlaufTooltip />} />
                <Bar yAxisId="mentions" dataKey="mentions" name="Erwähnungen" fill={farben.brand} fillOpacity={0.55} maxBarSize={28} />
                <Line yAxisId="upvotes" type="monotone" dataKey="upvotes" name="Upvotes" stroke={farben.gold} strokeWidth={1.6} dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Abschnitt>

      <Abschnitt titel={`Kursverlauf${currency ? ` (${currency})` : ''}`}>
        {!inDatenbank ? (
          <LeerHinweis>Dieser Ticker liegt nicht als Aktie in der Datenbank – kein Kursverlauf verfügbar.</LeerHinweis>
        ) : kursStatus === 'loading' ? (
          <p className="text-ink-muted">Lade Kursverlauf…</p>
        ) : kurseGefiltert.length === 0 ? (
          <LeerHinweis>Für den gewählten Zeitraum sind keine Tageswerte vorhanden.</LeerHinweis>
        ) : (
          <div className="rounded-xl border border-line bg-paper-soft p-2">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={kurseGefiltert} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="datum" tickFormatter={tickDatum} tick={achseStil} minTickGap={48} stroke={farben.line} />
                <YAxis tick={achseStil} width={56} stroke={farben.line} domain={['auto', 'auto']} tickFormatter={(v) => formatZahl(v, Math.abs(v) < 10 ? 2 : 0)} />
                <Tooltip content={<KursTooltip currency={currency} />} />
                <Line type="monotone" dataKey="schluss" name="Schlusskurs" stroke={farben.brand} strokeWidth={1.8} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Abschnitt>
    </>
  );
}

/** Subreddit-Tab: Aufschlüsselung der Erwähnungen + Top-Beiträge je Subreddit. */
function SubredditTab({ subreddits, subredditsStatus, posts, postsStatus }) {
  // Beiträge nach Subreddit gruppieren.
  const postsNachSub = useMemo(() => {
    const map = {};
    (posts || []).forEach((p) => {
      const key = p.subreddit;
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [posts]);

  // Karten aus der Vereinigung von Aufschlüsselung und Beiträgen bilden.
  const karten = useMemo(() => {
    const bekannt = new Set();
    const liste = (subreddits || []).map((e) => {
      bekannt.add(e.subredditFilter);
      return {
        subreddit: e.subredditFilter,
        mentions: e.mentions,
        upvotes: e.upvotes,
        posts: postsNachSub[e.subredditFilter] || [],
      };
    });
    Object.keys(postsNachSub)
      .filter((sub) => !bekannt.has(sub))
      .forEach((sub) => liste.push({ subreddit: sub, mentions: null, upvotes: null, posts: postsNachSub[sub] }));
    return liste;
  }, [subreddits, postsNachSub]);

  const maxMentions = Math.max(...karten.map((k) => k.mentions || 0), 1);
  const laedt = subredditsStatus === 'loading' || postsStatus === 'loading';

  if (karten.length === 0) {
    return laedt ? (
      <p className="text-ink-muted">Lade Subreddit-Daten…</p>
    ) : (
      <LeerHinweis>
        Noch keine Subreddit-Daten erfasst – die Aufschlüsselung baut sich mit den nächsten Abrufen auf.
      </LeerHinweis>
    );
  }

  return (
    <Abschnitt titel="Verteilung & Top-Beiträge je Subreddit">
      <SubredditKarten eintraege={karten} maxMentions={maxMentions} postsStatus={postsStatus} />
    </Abschnitt>
  );
}

/**
 * Verlauf eines Tickers mit zwei Reitern: „Verlauf" zeigt Erwähnungs- und
 * Kurs-Chart mit gemeinsamem Zeitraum-Regler, „Subreddits" die Aufschlüsselung
 * der Erwähnungen samt der besten Reddit-Beiträge je Subreddit.
 */
export default function RedditTickerView() {
  const { ticker } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const verlauf = useSelector(selectRedditVerlauf);
  const status = useSelector(selectRedditVerlaufStatus);
  const subreddits = useSelector(selectRedditSubreddits);
  const subredditsStatus = useSelector(selectRedditSubredditsStatus);
  const posts = useSelector(selectRedditPosts);
  const postsStatus = useSelector(selectRedditPostsStatus);
  const detail = useSelector(selectAktieDetail);
  const detailStatus = useSelector(selectAktieDetailStatus);

  const [tab, setTab] = useState('verlauf');

  useEffect(() => {
    dispatch(fetchRedditHistory(ticker));
    dispatch(fetchRedditSubreddits(ticker));
    dispatch(fetchRedditPosts(ticker));
  }, [dispatch, ticker]);

  // Reihe für den Chart (nur Punkte des angefragten Tickers).
  const reihe = useMemo(
    () => verlauf.filter((p) => p.ticker?.toUpperCase() === ticker?.toUpperCase()),
    [verlauf, ticker]
  );
  const aktuell = reihe[reihe.length - 1];
  const isinDb = aktuell?.inDatenbank ? aktuell.isin : null;

  // Kursdaten (chronologisch, nur Werte mit Schlusskurs) für den Kurs-Chart.
  const kurse = useMemo(() => {
    const werte = detail?.tageswerte ?? [];
    return [...werte]
      .reverse()
      .filter((t) => t.schlusskurs != null)
      .map((t) => ({ datum: t.datum, schluss: Number(t.schlusskurs) }));
  }, [detail]);

  // Kursdaten der zugehörigen Aktie laden, sobald die ISIN bekannt ist.
  useEffect(() => {
    if (isinDb) {
      dispatch(fetchAktieDetail(isinDb));
    }
  }, [dispatch, isinDb]);

  const handleZurAktie = () => {
    if (!isinDb) return;
    dispatch(selectAktie(isinDb));
    navigate(`/projekte/aktienanalyse/aktie/${isinDb}`);
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6 flex items-center justify-between">
        <BackToOverview />
        <button
          type="button"
          onClick={() => navigate('/projekte/reddit-buzz')}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
        >
          <span aria-hidden="true">←</span>
          <span>Ranking</span>
        </button>
      </div>

      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-3xl font-semibold text-ink">{ticker}</h1>
          {isinDb && (
            <button
              type="button"
              onClick={handleZurAktie}
              className="rounded-full bg-brand-500/10 px-3 py-1 font-mono text-xs font-medium text-brand-700 ring-1 ring-brand-500/25 transition-colors hover:bg-brand-500/20"
            >
              📊 Zur Aktienanalyse
            </button>
          )}
        </div>
        {aktuell?.name && <p className="mt-1 text-sm text-ink-muted">{aktuell.name}</p>}
      </header>

      {status === 'loading' && <p className="text-ink-muted">Lade Verlauf…</p>}
      {status === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Der Verlauf konnte nicht geladen werden.
        </p>
      )}

      {status === 'succeeded' && (
        <>
          {aktuell && (
            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Kachel label="Erwähnungen aktuell" value={formatZahl(aktuell.mentions, 0)} />
              <Kachel label="Upvotes aktuell" value={formatZahl(aktuell.upvotes, 0)} />
              <Kachel label="Rang" value={aktuell.rang != null ? `#${aktuell.rang}` : '–'} />
            </div>
          )}

          <div className="mb-6 flex gap-1 border-b border-line">
            <TabButton active={tab === 'verlauf'} onClick={() => setTab('verlauf')}>
              Verlauf
            </TabButton>
            <TabButton active={tab === 'subreddits'} onClick={() => setTab('subreddits')}>
              Subreddits
            </TabButton>
          </div>

          {tab === 'verlauf' && (
            <VerlaufTab
              reihe={reihe}
              kurse={kurse}
              currency={detail?.currency}
              inDatenbank={!!isinDb}
              kursStatus={detailStatus}
            />
          )}
          {tab === 'subreddits' && (
            <SubredditTab
              subreddits={subreddits}
              subredditsStatus={subredditsStatus}
              posts={posts}
              postsStatus={postsStatus}
            />
          )}
        </>
      )}
    </div>
  );
}
